import { EMPLOYEE_PROFILE_STEPS, type EmployeeProfileStepKey } from '../types';

interface ProfileStepperProps {
  activeStep: EmployeeProfileStepKey;
  /** Called when a step circle is chosen. Omit to render a stepper that cannot navigate. */
  onSelect?: (step: EmployeeProfileStepKey) => void;
  /** Steps after this index are disabled (Add Employee: only Step 1 exists until the employee is saved). */
  enabledThroughIndex?: number;
  disabledHint?: string;
}

/**
 * The numbered step header of the Employee Profile Setup, as drawn in the admin Figma: four steps, the current one ringed, the steps already passed
 * filled green with a green line to the next, the rest grey. "Passed" means the admin has moved beyond that step in this wizard; it is progress through
 * the screens, NOT a claim that the step's data is complete (readiness lives in the Setup Summary, which is reached from the readiness chip and its
 * "Setup summary" link, not drawn as a fifth circle). On the Setup Summary every step reads as passed. The accessible name stays "N. Label".
 */
export function ProfileStepper({ activeStep, onSelect, enabledThroughIndex = EMPLOYEE_PROFILE_STEPS.length - 1, disabledHint }: ProfileStepperProps) {
  const activeIndex = EMPLOYEE_PROFILE_STEPS.findIndex((s) => s.key === activeStep);
  const visibleSteps = EMPLOYEE_PROFILE_STEPS.filter((s) => s.key !== 'setup-summary');
  return (
    <nav className="epx-steps" aria-label="Employee Profile steps">
      <ol>
        {visibleSteps.map((s, index) => {
          const active = index === activeIndex;
          const done = index < activeIndex;
          const disabled = index > enabledThroughIndex;
          return (
            <li
              key={s.key}
              className={`epx-steps__item${active ? ' epx-steps__item--active' : ''}${done ? ' epx-steps__item--done' : ''}${disabled ? ' epx-steps__item--disabled' : ''}`}
            >
              <button
                type="button"
                aria-label={`${index + 1}. ${s.label}`}
                aria-current={active ? 'step' : undefined}
                disabled={disabled}
                title={disabled ? disabledHint : undefined}
                onClick={() => onSelect?.(s.key)}
              >
                <span className="epx-steps__circle" aria-hidden="true">
                  {index + 1}
                </span>
                <span className="epx-steps__label" aria-hidden="true">
                  {s.label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
